import APIService from "../components/app/services/APIService";

const BASE_URL = "/api/v1/demo/admin";

/**
 * Frontend API service for administering non-event TEST users (e.g. the Chrome
 * Web Store browser-extension reviewer seat). All endpoints require platform_admin.
 *
 * <p>Test users reuse the shared event-demo seat lifecycle endpoints
 * (deactivate / reactivate / extend / reset-password / list) but are created
 * through type-specific create endpoints. New test-user types only need their own
 * `create*` method here plus a config entry in TestUserAdmin.js.</p>
 */
const TestUserAdminService = {
  // ── Create (per test-user type) ──

  /**
   * Provision a Chrome Web Store browser-extension reviewer seat. The backend
   * auto-increments the seat code as ext-review-N. Restricted server-side to the
   * extension endpoints by ExtensionReviewAccessFilter.
   *
   * Uses the shared /full-access/seat/create path with a seatType discriminator so
   * it rides the already-deployed + API-Gateway-routed endpoint (no new gateway route).
   * @param {string} label - Optional label (defaults to "Chrome Web Store Review")
   * @param {string} expiresAt - ISO datetime string (required)
   * @param {string} [startsAt] - ISO datetime string (optional, defaults to now)
   */
  createExtensionReviewSeat: (label, expiresAt, startsAt = null) =>
    APIService.post(`${BASE_URL}/full-access/seat/create`, {
      seatType: "EXTENSION_REVIEW",
      label,
      expiresAt,
      ...(startsAt ? { startsAt } : {}),
    }),

  // ── Shared lifecycle (all test-user seat types) ──

  deactivateSeat: (seatCode) =>
    APIService.post(`${BASE_URL}/full-access/seat/deactivate`, { seatCode }),

  reactivateSeat: (seatCode) =>
    APIService.post(`${BASE_URL}/full-access/seat/reactivate`, { seatCode }),

  extendSeat: (seatCode, newExpiry) =>
    APIService.post(`${BASE_URL}/full-access/seat/extend`, {
      seatCode,
      newExpiry,
    }),

  resetPassword: (seatCode, newPassword) =>
    APIService.post(`${BASE_URL}/full-access/seat/reset-password`, {
      seatCode,
      newPassword,
    }),

  /**
   * Permanently delete a seat and every row it provisioned (user, account, group,
   * credentials, roles, subscription). Irreversible.
   *
   * Uses the shared /full-access/seat/deactivate path with action:"delete" so it
   * rides the already-deployed + API-Gateway-routed endpoint (no new gateway route).
   * @param {string} seatCode
   */
  deleteSeat: (seatCode) =>
    APIService.post(`${BASE_URL}/full-access/seat/deactivate`, {
      seatCode,
      action: "delete",
    }),

  // ── Query ──

  /**
   * List all provisioned seats (both EVENT_DEMO and test-user seat types). The
   * TestUserAdmin panel filters this by seatType client-side.
   * @returns {Promise<{success, count, data: EventDemoAccount[]}>}
   */
  listSeats: () => APIService.get(`${BASE_URL}/full-access/seats`),
};

export default TestUserAdminService;
